import * as z from 'zod'
import { ReservedResourcePhase } from 'src/api/api.generated'
import { TFunction } from 'i18next'
import { compareDates } from 'src/utils/date'

export function getEditRRSchema(t?: TFunction, f?: (date: Date) => string, isTimeResource?: boolean) {
  return z
    .object({
      phase: z.nativeEnum(ReservedResourcePhase),
      hotelDate: z.date(),
      start: z.date().nullable(),
      end: z.date().nullable(),
      time: z
        .object({
          from: z.date(),
          to: z.date(),
        })
        .optional(),
      resource: getResourceSchema(),
      guests: getGuestsSchema(t, isTimeResource),
      rateId: z.string().nullable(),
      price: z.number().nullable(),
      averagePrice: z.number().nullable(),
      availableResourceTypes: z
        .object({
          resourceTypeId: z.string(),
          overbooked: z.boolean(),
          guestsCompatible: z.boolean(),
          rateCompatible: z.boolean(),
          maxAdultsCompatible: z.boolean(),
        })
        .array(),
      availableResources: z
        .object({
          resourceId: z.string(),
        })
        .array(),
      resourceOccupancies: getResourceOccupancySchema().array(),
    })
    .superRefine((data, ctx) => {
      let continueValidation = true

      if (!data.start) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: t?.('validation.required_error'),
          path: ['start'],
        })
        continueValidation = false
      }
      if (!data.end) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: t?.('validation.required_error'),
          path: ['end'],
        })
        continueValidation = false
      }

      if (data.phase !== 'CHECKIN' && data.start && compareDates(data.start, data.hotelDate) < 0) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: t?.('pms.booking.reservation.guest.error.date_not_before', { date: f ? f(data.hotelDate) : data.hotelDate.toString() }),
          path: ['start'],
        })
      }
      if (!data.time && data.start && data.end && compareDates(data.start, data.end) >= 0) {
        if (compareDates(data.start, data.hotelDate) >= 0) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: t?.('pms.booking.reservation.reserved_resource_edit.error.invalid_date'),
            path: ['start'],
          })
        }

        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: t?.('pms.booking.reservation.reserved_resource_edit.error.invalid_date'),
          path: ['end'],
        })
        continueValidation = false
      }

      const lastResourceOccupancyStartDate = [...data.resourceOccupancies]?.pop()?.start
      if (data.resourceOccupancies.length > 1 && data.end && lastResourceOccupancyStartDate && compareDates(data.end, lastResourceOccupancyStartDate) <= 0) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: t?.('pms.booking.reservation.reserved_resource_edit.error.end_invalid_with_transfer'),
          path: ['end'],
        })
        continueValidation = false
      }

      if (!data.resource) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: t?.('validation.required_error'),
          path: ['resource'],
        })
        continueValidation = false
      }

      if (!data.rateId) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: t?.('validation.required_error'),
          path: ['rateId'],
        })
        continueValidation = false
      }

      if (!continueValidation) return
      const selectedResource = data.resourceOccupancies[data.resourceOccupancies.length - 1]
      if (!selectedResource) return

      const resourceType = data.availableResourceTypes.find((art) => art.resourceTypeId === selectedResource.assignedResourceTypeId)
      if (!resourceType) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: t?.('pms.booking.reservation.reserved_resource_edit.error.unavailable_resource_type'),
          path: ['resource'],
        })
        continueValidation = false
      }
      if (!continueValidation) return
      if (!resourceType) return

      if (!data.time && !resourceType.guestsCompatible) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: t?.('pms.booking.reservation.reserved_resource_edit.error.invalid_guests'),
          path: ['guests'],
        })
        continueValidation = false
      }

      if (!data.time && !resourceType.maxAdultsCompatible) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: t?.('pms.booking.reservation.reserved_resource_edit.error.invalid_adults'),
          path: ['guests', 'adults'],
        })
        continueValidation = false
      }

      // if (!resourceType.rateCompatible) {
      //   ctx.addIssue({
      //     code: z.ZodIssueCode.custom,
      //     message: t?.('pms.booking.reservation.reserved_resource_edit.error.invalid_rate'),
      //     path: ['rateId'],
      //   })
      //   continueValidation = false
      // }

      data.resourceOccupancies.map((ro, index) => {
        if (index > 0 && !ro.resourceId)
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: t?.('pms.booking.reservation.reserved_resource_edit.error_missing_resourceid'),
            path: [`resourceOccupancies[${index}].resourceId`],
          })
      })
      continueValidation = false

      if (!continueValidation) return

      if (selectedResource.resourceId && !data.availableResources.map((ar) => ar.resourceId).includes(selectedResource.resourceId)) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: t?.('pms.booking.reservation.reserved_resource_edit.error.unvailable_resource'),
          path: ['resource'],
        })
      }
    })
}

const editRRSchema = getEditRRSchema()
export type EditRRSchema = z.infer<typeof editRRSchema>

export function getResourceOccupancySchema() {
  return z.object({
    start: z.date(),
    end: z.date(),
    phase: z.nativeEnum(ReservedResourcePhase),
    reservedResourceTypeId: z.string(),
    assignedResourceTypeId: z.string().optional(),
    resourceId: z.string().optional(),
  })
}
const resourceOccupancySchema = getResourceOccupancySchema()
export type ResourceOccupancySchema = z.infer<typeof resourceOccupancySchema>

export function getGuestsSchema(t?: TFunction, isTimeResource?: boolean) {
  return z
    .object({
      adults: z.number().nullable(),
      children: z
        .object({
          childId: z.string(),
          noOccupancy: z.boolean(),
          quantity: z.number().nullable(),
        })
        .array(),
    })
    .superRefine((data, ctx) => {
      if (data.adults === null || data.adults === undefined) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: t?.('validation.required_error'),
          path: ['adults'],
        })
      } else if (!isTimeResource && data.adults < 1) {
        ctx.addIssue({
          code: z.ZodIssueCode.too_small,
          minimum: 1,
          inclusive: true,
          type: 'number',
          path: ['adults'],
        })
      }
      data.children.forEach((c, i) => {
        if (c.quantity !== null && c.quantity !== undefined && c.quantity < 0) {
          ctx.addIssue({
            code: z.ZodIssueCode.too_small,
            minimum: 0,
            inclusive: true,
            type: 'number',
            path: ['children', i, 'quantity'],
          })
        }
      })
    })
}

const guestsSchema = getGuestsSchema()
export type GuestsSchema = z.infer<typeof guestsSchema>

export function getResourceSchema() {
  return z
    .object({
      resourceTypeId: z.string(),
      resourceId: z.string().nullable(),
    })
    .nullable()
}
const resourceSchema = getResourceSchema()
export type ResourceSchema = z.infer<typeof resourceSchema>
